import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { ShieldPlus, Trash2, Clock, Check, Mail, Phone } from "lucide-react";
import ContactLink from "./ContactLink";

/**
 * Parent-side widget for the parent-child handshake.
 *
 * Renders under Family. Provides:
 *   • An input to invite a child account by email or phone.
 *   • A list of the parent's active + pending child links, with the
 *     ability to unlink an active child (only the parent may unlink).
 */
export default function ParentChildInvite() {
  const [rows, setRows] = useState([]);
  const [identifier, setIdentifier] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/parent/child-links");
      setRows(r.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    const v = identifier.trim();
    if (!v) return;
    const looksLikeEmail = /@/.test(v);
    const payload = looksLikeEmail ? { email: v.toLowerCase() } : { phone: v };
    setBusy(true);
    try {
      await api.post("/parent/invite-child", payload);
      toast.success("Invitation sent — waiting for child confirmation");
      setIdentifier("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't invite that user");
    } finally { setBusy(false); }
  };

  const unlink = async (row) => {
    if (!window.confirm(`Unlink ${row.other?.name || row.other?.email || "this child"}? They will no longer be trackable as your child.`)) return;
    try {
      await api.delete(`/parent/child-links/${row.id}`);
      toast.message("Child unlinked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unlink");
    }
  };

  return (
    <div data-testid="parent-child-invite" className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex items-center gap-3">
        <div className="size-10 rounded-xl bg-[#FFCC00]/15 grid place-items-center">
          <ShieldPlus className="text-[#FFCC00]" size={20}/>
        </div>
        <div>
          <div className="font-display font-bold">Invite a Child Account</div>
          <div className="text-[11px] text-zinc-500">Both sides must confirm — no manual "child" toggle needed.</div>
        </div>
      </div>

      <form onSubmit={invite} className="mt-4 flex gap-2">
        <input
          data-testid="parent-invite-input"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          placeholder="child's email or phone"
          className="flex-1 bg-zinc-900 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#007AFF]"
        />
        <button
          data-testid="parent-invite-submit"
          type="submit"
          disabled={busy || !identifier.trim()}
          className="bg-[#FFCC00] hover:bg-[#e6b800] text-black font-bold px-4 py-2.5 rounded-lg text-sm disabled:opacity-50"
        >
          Send
        </button>
      </form>

      {rows.length > 0 && (
        <div className="mt-5 space-y-2">
          <div className="label-eyebrow">Links</div>
          {rows.map((r) => {
            const isLinked = r.status === "linked";
            const isPending = r.status === "pending_link";
            const isDeclined = r.status === "declined";
            const isUnlinked = r.status === "unlinked";
            return (
              <div key={r.id} data-testid={`child-link-row-${r.id}`} className="p-3 rounded-xl border border-white/5 flex items-center gap-3">
                <div className="size-9 rounded-full bg-zinc-800 grid place-items-center font-display font-bold text-sm">
                  {(r.other?.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  {isLinked && r.other?.user_id ? (
                    <ContactLink userId={r.other.user_id} name={r.other.name || r.other.email} testId={`child-link-name-${r.id}`} className="font-semibold text-sm"/>
                  ) : (
                    <div className="font-semibold text-sm truncate">{r.other?.name || r.other?.email || "Unknown"}</div>
                  )}
                  <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1">
                    {r.other?.email && (<><Mail size={10}/>{r.other.email}</>)}
                  </div>
                </div>
                {isLinked ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#34C759] bg-[#34C759]/10 border border-[#34C759]/30 rounded-full px-2 py-1"><Check size={10} className="inline mr-0.5"/> Linked</span>
                ) : isPending ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#FFCC00] bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-full px-2 py-1"><Clock size={10} className="inline mr-0.5"/> Awaiting confirm</span>
                ) : (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 border border-white/10 rounded-full px-2 py-1">{r.status}</span>
                )}
                {isLinked && (
                  <button
                    data-testid={`unlink-${r.id}`}
                    onClick={() => unlink(r)}
                    className="size-8 rounded-full border border-white/10 grid place-items-center text-zinc-400 hover:text-[#FF3B30] hover:border-[#FF3B30]/40"
                    title="Unlink child"
                  >
                    <Trash2 size={13}/>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
